import { Card } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { GetServerSideProps } from 'next'
import { NextSeo } from 'next-seo'
import React, { useState } from 'react'
import dynamic from 'next/dynamic'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'

import FiltersProvider, { useFiltersContext } from '../src/contexts/Filters'
import { getCryptocurrencies, getFiatCurrencies } from '../src/services/currencies'
import { getExchangesWithPrices } from '../src/services/exchanges'
import { LoadState, Filters } from '../types/general'
import { urls, tabs } from '../src/utils/config'
import { Exchange } from '../types/exchange'
import { Cryptocurrency, Fiat } from '../types/currency'
import { useRouter } from 'next/router'

const Description = dynamic(() => import('../src/components/Subnav/Description'))
const ExchangeCard = dynamic(() => import('../src/components/Card/Exchange'))
const SeoText = dynamic(() => import('../src/components/SeoText/Exchange'))
const FilterContainer = dynamic(() => import('../src/components/Filters'))
import Background from '../src/components/Subnav/Background'
const LoadingIndicator = dynamic(() => import('../src/components/Loading'))
import Nav from '../src/components/Nav'
import Footer from '../src/components/Footer'

const useStyles = makeStyles(() => ({
  root: {
    maxWidth: '950px',
    margin: 'auto',
  },
  exchangeContainer: {
    marginBottom: '65px',
  },
  error: {
    padding: '50px',
    margin: '50px',
  },
}))

interface ExchangesContainerProps {
  initialLoadState: LoadState
  cryptocurrencies: Cryptocurrency[]
  fiatCurrencies: Fiat[]
  queryCurrency?: string
}

function ExchangeItem({ exchange }: { exchange: Exchange }) {
  const classes = useStyles({})
  const context = useFiltersContext()
  return (
    <div className={classes.exchangeContainer}>
      <ExchangeCard
        exchange={exchange}
        cryptocurrency={context.filters.cryptocurrency}
        fiat={context.filters.fiat}
      />
    </div>
  )
}

function ExchangesContainer({
  cryptocurrencies,
  initialLoadState,
  fiatCurrencies,
  queryCurrency,
}: ExchangesContainerProps): JSX.Element {
  const router = useRouter()
  const classes = useStyles({})
  const [loadState, setLoadState] = useState<LoadState>(initialLoadState)
  const [exchanges, setExchanges] = useState<Exchange[]>([])

  async function fetchExchanges(filters: Filters) {
    try {
      setLoadState(LoadState.LOADING)
      const data = await getExchangesWithPrices(filters)
      setLoadState(LoadState.LOADED)
      setExchanges(data)
    } catch (e) {
      setLoadState(LoadState.ERROR)
    }
  }

  function updateRoute(fiat: string) {
    setLoadState(LoadState.LOADING)
    router.push({
      pathname: router.pathname,
      query: {
        currency: fiat,
      },
    })
  }
  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.exchanges,
        },
      })}
    >
      {(): JSX.Element => (
        <FiltersProvider>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`The Best Cryptocurrency Exchanges - Cryptotesters`}
            description={`We compiled a list of the best cryptocurrency exchanges and let you compare prices in real-time! You'll find the cheapest exchange to buy Bitcoin, Ethereum and a range of other assets.`}
            openGraph={{
              url: `https://cryptotesters.com${urls.exchanges}`,
              title: `The Best Cryptocurrency Exchanges - Cryptotesters`,
              description: `We compiled a list of the best cryptocurrency exchanges and let you compare prices in real-time! You'll find the cheapest exchange to buy Bitcoin, Ethereum and a range of other assets.`,
              images: [
                {
                  url: 'https://cryptotesters.com/sharing.png',
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: 'https://cryptotesters.com/sharable_small.png',
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <Nav />
          <div className={classes.root}>
            <Background />
            <Description selectedTab={tabs.exchanges} />
            <FilterContainer
              cryptocurrencies={cryptocurrencies}
              onFilter={fetchExchanges}
              fiatCurrencies={fiatCurrencies}
              queryCurrency={queryCurrency}
              updateRoute={updateRoute}
            />
            {loadState === LoadState.ERROR && (
              <Card className={classes.error}>
                Sorry, there was a problem returning the exchanges
              </Card>
            )}
            {loadState !== LoadState.LOADED ? (
              <LoadingIndicator color="secondary" />
            ) : (
              exchanges.map((obj: Exchange, index) => <ExchangeItem exchange={obj} key={index} />)
            )}
            <SeoText />
          </div>
          <Footer />
        </FiltersProvider>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  let cryptocurrencies: Cryptocurrency[] = []
  let fiatCurrencies: Fiat[] = []
  let loadState = LoadState.IDLE
  const { currency } = context.query

  try {
    cryptocurrencies = await getCryptocurrencies('exchange')
    fiatCurrencies = await getFiatCurrencies()
    loadState = LoadState.LOADED
  } catch (e) {
    loadState = LoadState.ERROR
  }

  return {
    props: {
      cryptocurrencies,
      fiatCurrencies,
      initialLoadState: loadState,
      ...(currency ? { queryCurrency: currency } : {}),
    },
  }
}

export default ExchangesContainer
